import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import { Button, Card } from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type CreateTaskNodeData, type CreateTaskNodeType } from 'automations/types/nodeSchemas/actionNodes';
import { nodeSchemas } from 'automations/types/nodeTypes';
import { saveActionNode } from 'automations/utils/actionNode';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ZodError } from 'zod';

import styles from './CreateTaskContent.module.scss';
import ModifyTaskPayloadForm, { type ModifyTaskPayloadFormRef } from './ModifyTaskPayloadForm';

type CreateTaskContentType = CreateTaskNodeData['additionalOptions']['createTask'];

const defaultTaskData: Partial<CreateTaskContentType> = {
  associateWith: 'lead',
  owner: 'lead.owner',
  organizer: 'lead.owner',
};

export default function CreateTaskContent({
  node,
}: {
  node: NodeProps<CreateTaskNodeType>;
}) {
  const dispatch = useAutomationDispatch();
  const [loading, setLoading] = useState(false);
  const parentId = useSelector(getParentIdSelector(node.id));

  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const [taskData, setTaskData] = useState<Partial<CreateTaskContentType>>(
    node.data?.additionalOptions?.createTask ?? defaultTaskData,
  );
  const formRef = useRef<ModifyTaskPayloadFormRef>(null);

  const handleSubmit = useCallback(async () => {
    if (!formRef.current) return;
    setLoading(true);

    try {
      await formRef.current.validate();
      const parsedData = nodeSchemas.CreateTaskActionNodeData.parse({
        ...node.data,
        additionalOptions: {
          ...node.data?.additionalOptions,
          createTask: taskData,
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedData.additionalOptions,
      }));
      await dispatch(saveActionNode(node.id, parentId, contentMode));
      setMenuOpen(false);
    } catch (error: any) {
      if (error?.errorFields?.length) {
        message.error(error?.errorFields[0]?.errors[0]);
      } else {
        const errorMessage = error instanceof ZodError
          ? 'Add All Fields to Continue'
          : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [contentMode, dispatch, node.data, node.id, parentId, taskData, setMenuOpen]);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(`Action - Create #{${TranslationEntity.task}}`, [{ capitalize: true }]),
        destroyOnClose: true,
      }}
    >
      <Card title={contentMode === 'create' ? 'Set Action' : 'Edit Action'}>
        <ModifyTaskPayloadForm
          ref={formRef}
          taskData={taskData}
          setTaskData={setTaskData}
        />
      </Card>

      <Button
        type="primary"
        className={styles.submitButton}
        onClick={handleSubmit}
        loading={loading}
        disabled={loading}
      >
        Set Action
      </Button>
    </NodeMenu>
  );
}
